import React, { useEffect } from "react";
import { Provider, useSelector, useDispatch } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { getTags } from "actions/tags";
import { checkAppVersion, checkUserStatus } from "actions/user";
import AuthLayout from "layout";
import { getAllProducts } from "actions/products";
import Loader from "components/Loader";
import Dashboard from "pages/dashboard";
// import Catalog from "pages/catalog";
import BrowseCatalog from "pages/browse-catalog";
import EditProducts from "pages/edit-products";
import ProductDetail from "pages/product-detail";
// import AIMockUpGenerator from "pages/ai-mock-up-generator";
import Terms from "pages/terms";
import Support from "pages/support";
import Announcements from "pages/announcements";
import AdminScreenPrinting from "pages/admin-screen-printing";
import AccountSettings from "pages/account-settings";
// import MyStores from "pages/my-stores/stores";
// import MyStoresRegistration from "pages/my-stores/registration";
// import ConnectToShopify from "pages/connect-to-shopify";
// import ConnectToWooCommerce from "pages/connect-to-woo-commerce";
import { getGoogleFonts } from "actions/googleFonts";
import Orders from "pages/orders";
import OrderDetails from "pages/order-details";
import Home from "pages/home";
import AdminSettings from "pages/admin-settings";
import AdminUsers from "pages/admin-users";
import CreateProduct from "pages/create-product";
// import Inventory from "pages/inventory";
import Notifications from "pages/notifications";
// import BackgroundBurner from "pages/background-burner";
import DtfTransfersBySize from "pages/dtf-transfers/by-size";
import DtfGangSheetBuilder from "pages/dtf-transfers/gang-sheet";
import DtfCart from "pages/dtf-transfers/cart";
import DtfCheckout from "pages/dtf-transfers/checkout";
import DtfOrders from "pages/dtf-transfers/orders";
import AdminDtfOrders from "pages/admin-dtf-orders";
import DtgPlus from "pages/dtg-plus";
import HowToConnectBigCommerce from "pages/how-to-connect-big-commerce";
// import SalesReport from "pages/sales-report";
import NewsRoom from "pages/news-room";
import OrderStock from "pages/order-stock";
import FulfillmentDays from "pages/fulfillment-days";
import SubmittedStockOrders from "pages/submitted-stock-orders";
import WebPages from "pages/web-pages";
import IPProgram from "pages/ip-program";
// import SyncProducts from "pages/my-stores/sync-products";
// import SyncProduct from "pages/sync-product";
import { ROUTES } from "shared/constants/routes";
import { store, persistor } from "store";
// import Products from "pages/my-stores/products";
import Warehouse from "pages/warehouse";
// import VerifyArtFiles from "pages/verify-art-files";
import ErrorModal from "components/ErrorModal";
import { getWebPage } from "actions/webPages";
import { AUTH_LOGOUT } from "actions/auth";
import { INITIAL_SYNC_PRODUCTS } from "actions/sync-products";
import { INITIATE_STORES } from "actions/stores";
import YotiVerificationModal from "components/YotiVerificationModal";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "driver.js/dist/driver.css";

const HomeRoute = () => {
  const auth = useSelector((state) => state?.auth);

  if (auth?.authenticated) {
    return (
      <Navigate
        to={
          auth?.profile?.IPUser
            ? ROUTES.IP_PROGRAM.LINK
            : ROUTES.DASHBOARD.LINK
        }
        replace
      />
    );
  }

  return <Home />;
};

const App = () => {
  const dispatch = useDispatch();
  const loaderVisibility = useSelector((state) => state?.loader?.visibility);
  const auth = useSelector((state) => state?.auth);
  const products = useSelector((state) => state?.products?.data?.length);
  const tags = useSelector((state) => !!state?.tags?.data?.length);
  const googleFonts = useSelector((state) => state?.googleFonts?.data);
  const yotiModallVisibility = useSelector((state) => state?.yoti?.visibility);

  useEffect(() => {
    const fetchGoogleFonts = async () => {
      await dispatch(getGoogleFonts());
    };
    if (!googleFonts?.items?.length) fetchGoogleFonts();
    if (googleFonts) {
      const fonts = googleFonts?.items?.filter(
        (d) => d?.version === "v15" || d?.version === "v14"
      );
      let family = "";
      for (let i = 0; i < fonts?.length; i++) {
        family += `family=${fonts[i].family?.replace(/\s/g, "+")}&`;
      }
      family += "display=swap";
      var style = document.createElement("link");
      style.rel = "stylesheet";
      style.href = `https://fonts.googleapis.com/css2?${family}`;
      document.getElementsByTagName("head")[0].append(style);
    }
  }, [googleFonts]);

  useEffect(() => {
    const getUserStatusData = async () => {
      const data = await dispatch(checkUserStatus(auth?.profile?.emailid));
      if (data?.Status === "In-Active") {
        dispatch({ type: INITIATE_STORES });
        dispatch({ type: AUTH_LOGOUT });
        dispatch({ type: INITIAL_SYNC_PRODUCTS });
      }
    };

    if (auth?.authenticated) {
      getUserStatusData();
    }
  }, [auth, dispatch]);

  useEffect(() => {
    const loadData = () => {
      if (!products) {
        return dispatch(getAllProducts());
      }

      if (!tags) {
        return dispatch(getTags());
      }
    };
    loadData();
  }, [dispatch, products, tags]);

  useEffect(() => {
    const saveAppVersion = async () => {
      const version = await dispatch(checkAppVersion());
      window.localStorage.setItem("appVersion", version?.CurrentAppVersion);
    };
    saveAppVersion();
  }, [dispatch]);

  useEffect(() => {
    const getWebPageData = async () => {
      await dispatch(getWebPage({ page: "LandingPage" }));
    };

    if (auth?.authenticated) {
      getWebPageData();
    }
  }, [auth, dispatch]);

  return (
    <React.Fragment>
      <Router>
        <Routes>
          <Route path={ROUTES.HOME.LINK} element={<HomeRoute />} />
          <Route path={ROUTES.ANNOUNCEMENTS.LINK} element={<Announcements />} />
          <Route path={ROUTES.TERMS_AND_SERVICES.LINK} element={<Terms />} />
          <Route path={ROUTES.NEWS_ROOM.LINK} element={<NewsRoom />} />
          <Route path={ROUTES.SUPPORT.LINK} element={<Support />} />

          <Route element={<AuthLayout />}>
            <Route path={ROUTES.DASHBOARD.LINK} element={<Dashboard />} />
            {/* <Route path={ROUTES.CATALOG.LINK} element={<Catalog />} />
            <Route
              path={ROUTES.AI_MOCK_UP_GENERATOR.LINK}
              element={<AIMockUpGenerator />}
            /> */}
            <Route path={ROUTES.BROWSE_CATALOG.LINK} element={<BrowseCatalog />} />
            <Route
              path={`${ROUTES.PRODUCT_DETAIL.LINK}/:id`}
              element={<ProductDetail />}
            />
            <Route
              path={ROUTES.ACCOUNT_SETTINGS.LINK}
              element={<AccountSettings />}
            />
            <Route
              path={ROUTES.HOW_TO_CONNECT_BIG_COMMERCE.LINK}
              element={<HowToConnectBigCommerce />}
            />
            {/* <Route path={ROUTES.MY_STORES.LINK} element={<MyStores />} />
            <Route path={ROUTES.MY_STORES_PRODUCTS.LINK} element={<Products />} />
            <Route
              path={ROUTES.MY_STORES_REGISTRATION.LINK}
              element={<MyStoresRegistration />}
            /> */}
            <Route
              path={ROUTES.ADMIN_SCREEN_PRINTING.LINK}
              element={<AdminScreenPrinting />}
            />
            {/* <Route
              path={ROUTES.CONNECT_TO_SHOPIFY.LINK}
              element={<ConnectToShopify />}
            /> */}
            <Route path={ROUTES.ORDERS.LINK} element={<Orders />} />
            <Route
              path={`${ROUTES.ORDER_DETAILS.LINK}/:orderId`}
              element={<OrderDetails />}
            />
            <Route
              path={`${ROUTES.ORDER_DETAILS.LINK}/:orderId/:shopName`}
              element={<OrderDetails />}
            />
            <Route path={ROUTES.ADMIN_SETTINGS.LINK} element={<AdminSettings />} />
            <Route path={ROUTES.ADMIN_USERS.LINK} element={<AdminUsers />} />
            <Route
              path={ROUTES.ADMIN_DTF_ORDERS.LINK}
              element={<AdminDtfOrders />}
            />
            <Route path={ROUTES.ADMIN_EDIT_PRODUCT.LINK} element={<EditProducts />} />
            <Route path={ROUTES.ADMIN_CREATE_PRODUCT.LINK} element={<CreateProduct />} />
            {/* <Route path={ROUTES.INVENTORY.LINK} element={<Inventory />} />
            <Route path={ROUTES.ADMIN_SALES_REPORT.LINK} element={<SalesReport />} />
            <Route path={ROUTES.USER_SALES_REPORT.LINK} element={<SalesReport />} /> */}
            <Route path={ROUTES.WAREHOUSE.LINK} element={<Warehouse />} />
            <Route path={ROUTES.ADMIN_DTG_PLUS.LINK} element={<DtgPlus />} />
            <Route path={ROUTES.ORDER_STOCK.LINK} element={<OrderStock />} />
            {/* <Route path={ROUTES.BACKGROUND_BURNER.LINK} element={<BackgroundBurner />} /> */}
            <Route
              path={ROUTES.DTF_TRANSFERS.LINK}
              element={<Navigate to={ROUTES.DTF_TRANSFERS_BY_SIZE.LINK} replace />}
            />
            <Route
              path={ROUTES.DTF_TRANSFERS_BY_SIZE.LINK}
              element={<DtfTransfersBySize />}
            />
            <Route
              path={ROUTES.DTF_TRANSFERS_GANG_SHEET.LINK}
              element={<DtfGangSheetBuilder />}
            />
            <Route path={ROUTES.DTF_TRANSFERS_CART.LINK} element={<DtfCart />} />
            <Route path={ROUTES.DTF_TRANSFERS_CHECKOUT.LINK} element={<DtfCheckout />} />
            <Route path={ROUTES.DTF_ORDERS.LINK} element={<DtfOrders />} />
            <Route path={ROUTES.FULFILLMENT_DAYS.LINK} element={<FulfillmentDays />} />
            <Route path={ROUTES.WEB_PAGES.LINK} element={<WebPages />} />
            {/* <Route
              path={ROUTES.CONNECT_TO_WOO_COMMERCE.LINK}
              element={<ConnectToWooCommerce />}
            /> */}
            <Route path={ROUTES.NOTIFICATIONS.LINK} element={<Notifications />} />
            <Route
              path={ROUTES.SUBMITTED_ORDER_STOCK.LINK}
              element={<SubmittedStockOrders />}
            />
            {/* <Route
              path={`${ROUTES.MY_STORES_SYNC_PRODUCT.LINK}/:productId/:marketPlaceType/:shopName`}
              element={<SyncProduct />}
            />
            <Route
              path={ROUTES.MY_STORES_SYNC_PRODUCTS.LINK}
              element={<SyncProducts />}
            /> */}
            <Route path={ROUTES.IP_PROGRAM.LINK} element={<IPProgram />} />
            <Route path={ROUTES.ADMIN_IP_PROGRAM.LINK} element={<IPProgram />} />
            {/* <Route path={ROUTES.VERIFY_ART_FILES.LINK} element={<VerifyArtFiles />} /> */}
          </Route>
        </Routes>
      </Router>
      {loaderVisibility && <Loader open={loaderVisibility} />}
      {yotiModallVisibility && <YotiVerificationModal />}
    </React.Fragment>
  );
};

const Wrapper = () => {
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <App />
        <ErrorModal />
      </PersistGate>
    </Provider>
  );
};

export default Wrapper;
